import { useEffect } from 'react';

/**
 * `⌘K` on macOS, `Ctrl+K` elsewhere — the standard names both, and both are accepted here on every
 * platform rather than being switched on a user-agent sniff: a person on a Mac with an external PC
 * keyboard reaches for Ctrl, and refusing them would be a platform guess dressed up as a feature.
 *
 * `Ctrl+K` is the browser's own "focus the search bar" shortcut in Chrome and Firefox, so
 * `preventDefault()` is required, not optional. It is called only when the combination actually
 * matches, so no other keystroke is intercepted.
 *
 * Bound on `document` rather than on a container: the palette must open from anywhere, including
 * while focus sits in a screen's own input.
 */
export function useCommandHotkey(onOpen: () => void): void {
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'k' && event.key !== 'K') {
        return;
      }
      if (!event.metaKey && !event.ctrlKey) {
        return;
      }
      // `Alt` held is a different combination — some keyboard layouts produce other characters
      // with it, and claiming those would break text entry.
      if (event.altKey) {
        return;
      }
      event.preventDefault();
      onOpen();
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onOpen]);
}
